import { supabase } from './supabase'
import { normalizeAssignedToId } from './leadHelpers'
import type { CustomerContact, CustomerNote } from '../components/customer/types'
import type { AdditionalAccess, PropertyNote } from '../components/property/types'

export interface ConversionContact {
  id: string
  first_name: string
  last_name: string
  title: string
  phone: string
  cell: string
  email: string
}

export interface ConversionProperty {
  name: string
  address: string
  address_line_2: string
  city: string
  state: string
  zip: string
  type: string
  unit_count: string
  work_type: string
  notes: string
  building_type: string
  units: string
  stories: string
  access_type: string
  access_info: string
  additional_access: AdditionalAccess[]
  payment_method: string
  sales_tax_status: string
  selected_contact_ids: string[]
  property_notes: PropertyNote[]
}

export interface ConversionData {
  company_name: string
  company_phone: string
  company_email: string
  company_website: string
  company_address: string
  company_address_line_2: string
  company_city: string
  company_state: string
  company_zip: string
  contact_first_name: string
  contact_last_name: string
  email: string
  phone: string
  cell: string
  status: string
  assigned_to: string
  payment_method: string
  sales_tax_status: string
  custom_fields: Record<string, string>
  customer_notes: CustomerNote[]
  contacts: ConversionContact[]
  properties: ConversionProperty[]
}

export const emptyConversionProperty = (): ConversionProperty => ({
  name: '',
  address: '',
  address_line_2: '',
  city: '',
  state: '',
  zip: '',
  type: '',
  unit_count: '',
  work_type: '',
  notes: '',
  building_type: '',
  units: '',
  stories: '',
  access_type: '',
  access_info: '',
  additional_access: [],
  payment_method: '',
  sales_tax_status: '',
  selected_contact_ids: [],
  property_notes: [],
})

export const emptyConversionData = (): ConversionData => ({
  company_name: '',
  company_phone: '',
  company_email: '',
  company_website: '',
  company_address: '',
  company_address_line_2: '',
  company_city: '',
  company_state: '',
  company_zip: '',
  contact_first_name: '',
  contact_last_name: '',
  email: '',
  phone: '',
  cell: '',
  status: 'onboarding',
  assigned_to: '',
  payment_method: '',
  sales_tax_status: '',
  custom_fields: {},
  customer_notes: [],
  contacts: [],
  properties: [],
})

/** Internal keys temporarily stored on lead.custom_fields during conversion — not user custom fields */
export const INTERNAL_CONVERSION_CUSTOM_FIELD_PREFIX = '_conversion_'

export function isInternalConversionCustomFieldKey(key: string): boolean {
  return key.startsWith(INTERNAL_CONVERSION_CUSTOM_FIELD_PREFIX)
}

/** Remove internal conversion keys from custom_fields for display/editing */
export function stripInternalConversionCustomFields(
  fields: Record<string, unknown> | null | undefined
): Record<string, string> {
  if (!fields || typeof fields !== 'object') return {}
  const result: Record<string, string> = {}
  for (const [key, val] of Object.entries(fields)) {
    if (isInternalConversionCustomFieldKey(key)) continue
    if (val != null && typeof val !== 'object') {
      result[key] = String(val)
    }
  }
  return result
}

/** Read wizard status/notes from internal conversion keys on a lead */
export function extractInternalConversionFields(fields: Record<string, unknown> | null | undefined): {
  status: string
  customer_notes: CustomerNote[]
  payment_method: string
  sales_tax_status: string
} {
  if (!fields || typeof fields !== 'object') {
    return { status: 'onboarding', customer_notes: [], payment_method: '', sales_tax_status: '' }
  }
  const status =
    typeof fields._conversion_status === 'string' && fields._conversion_status.trim()
      ? fields._conversion_status
      : 'onboarding'
  const customer_notes = Array.isArray(fields._conversion_customer_notes)
    ? (fields._conversion_customer_notes as CustomerNote[])
    : []
  const payment_method =
    typeof fields._conversion_payment_method === 'string' ? fields._conversion_payment_method : ''
  const sales_tax_status =
    typeof fields._conversion_sales_tax_status === 'string' ? fields._conversion_sales_tax_status : ''
  return { status, customer_notes, payment_method, sales_tax_status }
}

/** Return assigned_to only when the user exists in user_profiles (avoids FK violations) */
export async function resolveValidatedAssignedTo(value: unknown): Promise<string | null> {
  const id = normalizeAssignedToId(value)
  if (!id) return null
  const { data, error } = await supabase.from('user_profiles').select('id').eq('id', id).maybeSingle()
  if (error || !data?.id) return null
  return id
}

function resolveContactNames(contact: any): { first_name: string; last_name: string } {
  if (contact?.first_name || contact?.last_name) {
    return {
      first_name: contact.first_name || '',
      last_name: contact.last_name || '',
    }
  }
  if (contact?.name) {
    const parts = contact.name.trim().split(' ')
    return {
      first_name: parts[0] || '',
      last_name: parts.slice(1).join(' ') || '',
    }
  }
  return { first_name: '', last_name: '' }
}

function mapLeadContact(c: any, idx: number): ConversionContact {
  const names = resolveContactNames(c)
  return {
    id: c.id || `contact-${idx}`,
    first_name: names.first_name,
    last_name: names.last_name,
    title: c.title || '',
    phone: c.phone || '',
    cell: c.cell || '',
    email: c.email || '',
  }
}

export function populateConversionDataFromLead(lead: any): ConversionData {
  const normalizeBuildingType = (value: any): string => {
    const text = typeof value === 'string' ? value.trim() : ''
    if (!text) return ''

    // Backwards compatibility: old lead project type labels → property building types
    const lowered = text.toLowerCase()
    if (lowered === 'office building' || lowered === 'single office space') return 'Office'
    if (lowered === 'residential building' || lowered === 'single apartment') return 'Residential'
    if (lowered === 'other building' || lowered === 'other space') return 'Other'

    // Already normalized or custom
    return text
  }

  const primaryContact = lead.contacts?.[0] ?? null
  const primaryNames = primaryContact
    ? resolveContactNames(primaryContact)
    : {
        first_name: lead.contact_first_name || '',
        last_name: lead.contact_last_name || '',
      }

  const allContacts: ConversionContact[] =
    lead.contacts && Array.isArray(lead.contacts) && lead.contacts.length > 0
      ? lead.contacts.map(mapLeadContact)
      : primaryNames.first_name || primaryNames.last_name
        ? [
            {
              id: '1',
              first_name: primaryNames.first_name,
              last_name: primaryNames.last_name,
              title: '',
              phone: lead.phone || lead.company_phone || '',
              cell: '',
              email: lead.email || lead.company_email || '',
            },
          ]
        : []

  const properties: ConversionProperty[] =
    lead.projects?.length > 0
      ? lead.projects.map((p: any) => ({
          name: p.name || p.address || 'Property',
          address: p.address || '',
          address_line_2: p.address_line_2 || '',
          city: p.city || '',
          state: p.state || '',
          zip: p.zip || '',
          type: p.type || '',
          unit_count: p.unit_count || '',
          work_type: p.work_type || '',
          notes: p.notes || '',
          building_type: normalizeBuildingType(p.building_type || p.type),
          units: p.units || p.unit_count || '',
          stories: p.stories || '',
          access_type: p.access_type || '',
          access_info: p.access_info || '',
          additional_access: Array.isArray(p.additional_access)
            ? p.additional_access.map((a: any, i: number) => ({
                id: a.id || `access-${i}`,
                location: a.location || '',
                access_type: a.access_type || '',
                access_info: a.access_info || '',
              }))
            : [],
          payment_method: p.payment_method || '',
          sales_tax_status: p.sales_tax_status || '',
          selected_contact_ids: Array.isArray(p.selected_contact_ids) ? p.selected_contact_ids : [],
          property_notes: Array.isArray(p.property_notes)
            ? p.property_notes
            : p.notes
              ? [{ timestamp: new Date().toISOString(), note: p.notes }]
              : [],
        }))
      : []

  const rawCustomFields =
    lead.custom_fields && typeof lead.custom_fields === 'object' ? { ...lead.custom_fields } : {}
  const { status, customer_notes, payment_method, sales_tax_status } =
    extractInternalConversionFields(rawCustomFields)
  const customFields = stripInternalConversionCustomFields(rawCustomFields)

  return {
    company_name: lead.company_name || '',
    company_phone: lead.company_phone || '',
    company_email: lead.company_email || '',
    company_website: lead.company_website || '',
    company_address: lead.company_address || lead.address || '',
    company_address_line_2: lead.company_address_line_2 || '',
    company_city: lead.company_city || lead.city || '',
    company_state: lead.company_state || lead.state || '',
    company_zip: lead.company_zip || lead.zip_code || lead.zip || '',
    contact_first_name: primaryNames.first_name,
    contact_last_name: primaryNames.last_name,
    email: primaryContact?.email || lead.email || lead.company_email || '',
    phone: primaryContact?.phone || lead.phone || lead.company_phone || '',
    cell: primaryContact?.cell || '',
    status,
    assigned_to: lead.assigned_to || '',
    payment_method,
    sales_tax_status,
    custom_fields: customFields,
    customer_notes,
    contacts: allContacts,
    properties,
  }
}

export function buildUpdatedContacts(lead: any, data: ConversionData): any[] {
  if (data.contacts.length > 0) {
    return data.contacts.map((c) => {
      const original = lead.contacts?.find((lc: any) => lc.id === c.id) ?? {}
      const name = `${c.first_name} ${c.last_name}`.trim()
      return {
        ...original,
        id: c.id,
        name,
        first_name: c.first_name,
        last_name: c.last_name,
        title: c.title || undefined,
        phone: c.phone,
        cell: c.cell,
        email: c.email,
      }
    })
  }

  const fullName = `${data.contact_first_name} ${data.contact_last_name}`.trim()
  const primaryContact = lead.contacts?.[0] ?? null
  if (primaryContact) {
    return [
      {
        ...primaryContact,
        name: fullName,
        first_name: data.contact_first_name,
        last_name: data.contact_last_name,
        email: data.email,
        phone: data.phone,
        cell: data.cell,
      },
    ]
  }

  return []
}

export function conversionContactsToCustomerContacts(contacts: ConversionContact[]): CustomerContact[] {
  return contacts.map((c) => ({
    id: c.id,
    first_name: c.first_name,
    last_name: c.last_name,
    name: `${c.first_name} ${c.last_name}`.trim(),
    title: c.title,
    phone: c.phone,
    cell: c.cell,
    email: c.email,
  }))
}

export async function applyConversionDataToLead(leadId: string, lead: any, data: ConversionData) {
  const updatedContacts = buildUpdatedContacts(lead, data)

  const { error } = await supabase
    .from('leads')
    .update({
      company_name: data.company_name,
      company_phone: data.company_phone,
      company_email: data.company_email,
      company_website: data.company_website,
      company_address: data.company_address,
      company_address_line_2: data.company_address_line_2,
      company_city: data.company_city,
      company_state: data.company_state,
      company_zip: data.company_zip,
      contact_first_name: data.contacts[0]?.first_name || data.contact_first_name,
      contact_last_name: data.contacts[0]?.last_name || data.contact_last_name,
      email: data.contacts[0]?.email || data.email,
      phone: data.contacts[0]?.phone || data.phone,
      assigned_to: await resolveValidatedAssignedTo(data.assigned_to),
      custom_fields: (() => {
        const base =
          lead.custom_fields && typeof lead.custom_fields === 'object' ? { ...lead.custom_fields } : {}
        const userFields = stripInternalConversionCustomFields(data.custom_fields)
        return {
          ...stripInternalConversionCustomFields(base),
          ...userFields,
          _conversion_status: data.status,
          _conversion_customer_notes: data.customer_notes,
          _conversion_payment_method: data.payment_method,
          _conversion_sales_tax_status: data.sales_tax_status,
        }
      })(),
      contacts: updatedContacts,
      projects: data.properties.map((prop) => ({
        name: prop.name,
        address: prop.address,
        address_line_2: prop.address_line_2,
        city: prop.city,
        state: prop.state,
        zip: prop.zip,
        type: prop.type || prop.building_type,
        unit_count: prop.unit_count || prop.units,
        work_type: prop.work_type,
        notes: prop.notes,
        building_type: prop.building_type,
        units: prop.units,
        stories: prop.stories,
        access_type: prop.access_type,
        access_info: prop.access_info,
        additional_access: prop.additional_access,
        payment_method: prop.payment_method,
        sales_tax_status: prop.sales_tax_status,
        selected_contact_ids: prop.selected_contact_ids,
        property_notes: prop.property_notes,
      })),
    })
    .eq('id', leadId)

  if (error) throw error
}

/** Remove internal conversion keys from a lead after a failed conversion attempt */
export async function cleanupInternalConversionCustomFieldsOnLead(leadId: string, lead: any) {
  const raw =
    lead.custom_fields && typeof lead.custom_fields === 'object' ? { ...lead.custom_fields } : {}
  const cleaned = stripInternalConversionCustomFields(raw)
  const { error } = await supabase
    .from('leads')
    .update({
      custom_fields: Object.keys(cleaned).length > 0 ? cleaned : null,
    })
    .eq('id', leadId)
  if (error) throw error
}
